/**
 * CSV helpers for accountant-friendly exports.
 *
 * Output rules (the same for every merchant, regardless of UI locale):
 *   - Comma separator (RFC 4180; the universal interchange format).
 *   - UTF-8 with BOM, so Excel-on-Windows opens accented characters correctly.
 *   - Decimal point (`.`) for numbers, always. Localized decimals confuse importers.
 *   - ISO 8601 dates (`YYYY-MM-DD` or full timestamp) — sortable and unambiguous.
 *   - CRLF line endings, the RFC 4180 default Excel expects.
 *
 * EU users who double-click the file in localized Excel will see all columns
 * collapsed into column A. The fix is a one-time tip: use Data → Get Data →
 * From Text/CSV. We surface that hint in the export UI, not here.
 */

export interface CsvColumn<T> {
  /** Column header as it appears in the CSV. */
  header: string;
  /** Cell value extractor. Return `null`/`undefined` for blank cells. */
  value: (row: T) => string | number | null | undefined;
}

const UTF8_BOM = '﻿';
const LINE_END = '\r\n';

function escapeCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  let s = String(value);
  if (typeof value === 'number') {
    // Force `.` decimals; Excel-EU will localize on display but the data is consistent.
    s = Number.isFinite(value) ? value.toString() : '';
  }
  // Quote on `;` too even though our field separator is `,`. Excel-EU's auto-
  // detector counts both delimiters and may flip the whole file to `;` mode if
  // it sees any `;` outside quotes — which then mis-splits every row in the
  // file, shifting columns and looking like missing data. Quoting `;`-bearing
  // values keeps the file robust regardless of which separator the importer
  // settles on.
  if (/[",;\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Render rows as a CSV string (no BOM). For the file payload use {@link buildCsvBlob}.
 */
export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map((c) => escapeCell(c.header)).join(',');
  const body = rows.map((row) => columns.map((c) => escapeCell(c.value(row))).join(',')).join(LINE_END);
  return body ? header + LINE_END + body + LINE_END : header + LINE_END;
}

export function buildCsvBlob<T>(rows: T[], columns: CsvColumn<T>[]): Blob {
  return new Blob([UTF8_BOM + toCsv(rows, columns)], { type: 'text/csv;charset=utf-8' });
}

/**
 * Trigger a browser download of a CSV file. No-op outside the browser.
 */
export function downloadCsv<T>(filename: string, rows: T[], columns: CsvColumn<T>[]): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = buildCsvBlob(rows, columns);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Format a Date or ISO string as `YYYY-MM-DD`, or '' if not parseable. */
export function isoDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Format a Date or ISO string as `YYYY-MM-DDTHH:MM:SS`, or '' if not parseable. */
export function isoTimestamp(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 19);
}

/** Round to 2 decimals; returns a number, not a string. */
export function money(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

/**
 * Like money(), but returns null instead of 0 when the input is null /
 * undefined / non-finite. Use this for accounting columns where missing
 * data must NOT be reported as a real zero.
 */
export function moneyOrNull(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return Math.round(value * 100) / 100;
}
